import React, { useEffect, useState } from 'react';
import { Check, Edit, X } from '../Icons';

/**
 * Single proposed change card. Pure presentational + local edit-form state;
 * all destructive/network actions are passed in via props.
 *
 * Props:
 *   change         — the pending-change object
 *   isEditing      — whether this card is currently in edit mode
 *   onConfirm(id)
 *   onDelete(id)
 *   onEditOpen(id)
 *   onEditCancel()
 *   onEditSave(id, fields)
 */
export default function ProposalCard({
  change,
  isEditing,
  onConfirm,
  onDelete,
  onEditOpen,
  onEditCancel,
  onEditSave,
}) {
  // Initial values used only for the very first render — subsequent prop changes
  // re-derive in the effect below.
  const initialName = (change.activity_name || (change.change_data && change.change_data.name) || change.summary || '').toString();
  const initialMin  = Number(change.suggested_duration_minutes ?? (change.change_data && change.change_data.duration_minutes) ?? 30);
  const initialToday = !!change.for_today;

  const [name, setName] = useState(initialName);
  const [minutes, setMinutes] = useState(initialMin);
  const [forToday, setForToday] = useState(initialToday);
  const [saving, setSaving] = useState(false);

  // Re-sync local fields when entering edit mode OR when the underlying change prop swaps
  // (same component instance, fresh change_id). Re-derive inside the effect so we always
  // pick up the LATEST props (useState initial values are captured on mount only).
  useEffect(() => {
    if (isEditing) {
      const nextName = (change.activity_name || (change.change_data && change.change_data.name) || change.summary || '').toString();
      const nextMin  = Number(change.suggested_duration_minutes ?? (change.change_data && change.change_data.duration_minutes) ?? 30);
      const nextToday = !!change.for_today;
      setName(nextName);
      setMinutes(nextMin);
      setForToday(nextToday);
    }
    // change_data intentionally omitted — change_id is a stable primitive key
    // for re-derivation; depending on change_data (an object) would re-run on
    // every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, change.change_id, change.activity_name, change.summary, change.suggested_duration_minutes, change.for_today]);

  const titleText = change.activity_name || change.summary || 'Proposed change';
  const dur = Number(change.suggested_duration_minutes);

  const minutesValid = Number.isFinite(minutes) && minutes >= 1;

  const handleSave = async () => {
    if (!name || !name.trim() || !minutesValid) return;
    setSaving(true);
    try {
      await onEditSave(change.change_id, {
        name: name.trim(),
        duration_minutes: Math.round(minutes),
        for_today: !!forToday,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`ref-thread-item ref-proposal-card fade-up-enter ${isEditing ? 'is-editing' : ''}`}>
      <div className="ref-proposal-eyebrow eyebrow-gold">PROPOSED</div>
      <div className="ref-proposal-title">{titleText}</div>
      <div className="ref-proposal-meta">
        {Number.isFinite(dur) && dur > 0 && (
          <span className="ref-proposal-pill">{dur} min</span>
        )}
        {change.for_today && (
          <span className="ref-proposal-pill ref-proposal-pill-gold">Today</span>
        )}
      </div>

      {!isEditing && (
        <div className="ref-proposal-actions">
          <button
            type="button"
            className="btn-confirm"
            onClick={() => onConfirm(change.change_id)}
            aria-label="Confirm proposal"
          >
            <Check size={14} />
            <span>Confirm</span>
          </button>
          <button
            type="button"
            className="btn-edit"
            onClick={() => onEditOpen(change.change_id)}
            aria-label="Edit proposal"
          >
            <Edit size={14} />
            <span>Edit</span>
          </button>
          <button
            type="button"
            className="btn-delete"
            onClick={() => onDelete(change.change_id)}
            aria-label="Delete proposal"
          >
            <X size={14} />
            <span>Delete</span>
          </button>
        </div>
      )}

      {isEditing && (
        <div className="ref-proposal-edit-form">
          <label className="ref-edit-field">
            <span className="ref-edit-label">Activity</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              autoFocus
            />
          </label>
          <label className="ref-edit-field">
            <span className="ref-edit-label">Duration (min)</span>
            <input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              min="1"
              max="600"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </label>
          <label className="ref-edit-toggle">
            <input
              type="checkbox"
              checked={forToday}
              onChange={(e) => setForToday(e.target.checked)}
            />
            <span>Schedule for today</span>
          </label>
          <div className="ref-proposal-actions">
            <button
              type="button"
              className="btn-confirm"
              onClick={handleSave}
              disabled={saving || !name.trim() || !minutesValid}
            >
              <Check size={14} />
              <span>{saving ? 'Saving…' : 'Save'}</span>
            </button>
            <button
              type="button"
              className="btn-edit"
              onClick={onEditCancel}
              disabled={saving}
            >
              <span>Cancel</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
